import { useState, useEffect, useCallback } from 'react';
import { Mail, Phone, FileText, Trash2, RefreshCw, Inbox, ExternalLink } from 'lucide-react';
import './ContactSubmissions.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const TYPE_LABEL = { werkgever: 'Ik zoek personeel', werknemer: 'Ik zoek werk' };

// Telefoonnummer -> WhatsApp-nummer (NL-nummers naar landcode 31).
function waNumber(phone) {
  let d = String(phone || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('00')) d = d.slice(2);
  else if (d.startsWith('0')) d = `31${d.slice(1)}`;
  return d;
}

export default function ContactSubmissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | werkgever | werknemer | unread

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/contact-submissions');
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('load contact submissions', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id, isRead) => {
    try {
      await authFetch(`/api/contact-submissions/${id}/read`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_read: isRead }),
      });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, is_read: isRead ? 1 : 0 } : it)));
    } catch (err) { console.error('markRead', err); }
  };

  const remove = async (id) => {
    if (!window.confirm('Deze inschrijving definitief verwijderen?')) return;
    try {
      await authFetch(`/api/contact-submissions/${id}`, { method: 'DELETE' });
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err) { console.error('remove', err); }
  };

  const filtered = items.filter((it) => {
    if (filter === 'unread') return !it.is_read;
    if (filter === 'werkgever' || filter === 'werknemer') return it.type === filter;
    return true;
  });

  const unreadCount = items.filter((it) => !it.is_read).length;

  return (
    <div className="contact-submissions">
      <div className="cs-header">
        <div>
          <h2>Inschrijvingen</h2>
          <p className="cs-sub">Contactformulieren van de website{unreadCount > 0 ? ` — ${unreadCount} ongelezen` : ''}</p>
        </div>
        <button className="cs-refresh" onClick={load} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
          Vernieuwen
        </button>
      </div>

      <div className="cs-filters">
        {[
          { key: 'all', label: `Alle (${items.length})` },
          { key: 'unread', label: `Ongelezen (${unreadCount})` },
          { key: 'werkgever', label: 'Ik zoek personeel' },
          { key: 'werknemer', label: 'Ik zoek werk' },
        ].map((f) => (
          <button
            key={f.key}
            className={`cs-filter ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="cs-body">
        <div className="cs-list">
          {loading && <div className="cs-empty">Laden…</div>}
          {!loading && filtered.length === 0 && (
            <div className="cs-empty"><Inbox size={28} /><span>Geen inschrijvingen</span></div>
          )}
          {!loading && filtered.map((it) => {
            const wa = waNumber(it.phone);
            return (
              <div key={it.id} className={`cs-item ${it.is_read ? '' : 'unread'}`}>
                {/* Openen in een nieuw tabblad, zodat de lijst blijft staan. */}
                <a
                  className="cs-item-main"
                  href={`/dashboard/aanvragen/${it.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { if (!it.is_read) markRead(it.id, true); }}
                >
                  <div className="cs-item-top">
                    <span className="cs-item-name">{it.name}</span>
                    <span className="cs-item-date">{formatDate(it.created_at)}</span>
                  </div>
                  <div className="cs-item-meta">
                    <span className={`cs-badge ${it.type}`}>{TYPE_LABEL[it.type] || it.type}</span>
                    {it.company && <span className="cs-item-company">{it.company}</span>}
                    {it.files && it.files.length > 0 && (
                      <span className="cs-attach"><FileText size={13} /> {it.files.length}</span>
                    )}
                    {!it.is_read && <span className="cs-dot" />}
                  </div>
                  <p className="cs-item-preview">{it.subject || it.message}</p>
                </a>

                <div className="cs-item-actions">
                  <a className="cs-mini" href={`mailto:${it.email}`} title={it.email}>
                    <Mail size={14} /> <span className="cs-mini-label">Mail</span>
                  </a>
                  {it.phone && (
                    <a className="cs-mini" href={`tel:${it.phone}`} title={it.phone}>
                      <Phone size={14} /> <span className="cs-mini-label">Bel</span>
                    </a>
                  )}
                  {wa && (
                    <a className="cs-mini cs-mini-wa" href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" title="WhatsApp">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.117 1.528 5.845L0 24l6.335-1.502A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.017-1.374l-.36-.214-3.732.885.936-3.619-.234-.373A9.818 9.818 0 1112 21.818z"/></svg>
                      <span className="cs-mini-label">WhatsApp</span>
                    </a>
                  )}
                  <a className="cs-mini" href={`/dashboard/aanvragen/${it.id}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={14} /> <span className="cs-mini-label">Openen</span>
                  </a>
                  <button className="cs-mini cs-mini-danger" onClick={() => remove(it.id)} title="Verwijderen">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
